import React from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import useOpportunityStatuses from "@/opportunity/useOpportunityStatuses";
import { Opportunity } from "@/opportunity/opportunity";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleItem } from "@/components/ui/toggle-group";
import { formatISO } from "date-fns";
import { useOpportunityChangeStatusMutation } from "@/opportunity/api";

interface FormData {
  occurredOn: string;
  targetStatus: string;
  reason?: string;
  comments: string;
}

const ChangeStatusModal = ({
                             isOpen,
                             onClose,
                             opportunity
                           }: {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
}) => {
  const changeStatusMutation = useOpportunityChangeStatusMutation(opportunity);
  const { data: statusOptions } = useOpportunityStatuses();

  const { control, handleSubmit, reset, formState: { isValid } } = useForm<FormData>({
    defaultValues: {
      occurredOn: formatISO(new Date(), { representation: "date" }),
      targetStatus: "",
      reason: "",
      comments: ""
    }
  });

  const targetStatus = useWatch({ control, name: "targetStatus" });
  const selectedStatus = statusOptions?.find(status => status.name === targetStatus);

  if (!isOpen) return null;

  const onSubmit = (data: FormData) => {
    changeStatusMutation.mutate({
      opportunityId: opportunity.id,
      targetStatus: data.targetStatus,
      reason: data.reason || undefined,
      comments: data.comments || undefined,
      occurredOn: new Date(data.occurredOn)
    });
    handleClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center text-sm">
      <div className="bg-white p-4 rounded shadow-lg flex flex-col gap-2">
        <h2 className="text-lg">Change issue status</h2>
        <div className="bg-gray-50 p-2 rounded-lg text-gray-500 flex flex-col gap-2">
          <p>
            <span className="font-semibold">{opportunity.category}</span> at <span className="font-semibold">{opportunity.location.name}</span>
          </p>
          <p>
            Identified: <span className="font-semibold">{opportunity.identified_date}</span>
          </p>
          <p>
            Current status: <span className="font-semibold">{opportunity.status}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
          <div>
            <label htmlFor="occurredOn" className="text-xs">
              Occurred on<span className="text-red-500">*</span>
            </label>
            <Controller
              name="occurredOn"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <input
                  {...field}
                  type="date"
                  className="border p-2 w-full"
                />
              )}
            />
          </div>

          <div>
            <label htmlFor="targetStatus" className="text-xs">
              New status<span className="text-red-500">*</span>
            </label>
            <Controller
              name="targetStatus"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions?.map((status) => (
                      <SelectItem
                        key={status.name}
                        value={status.name}
                        disabled={status.name === opportunity.status}
                      >
                        <p>{status.name}</p>
                        <p className="text-gray-500">{status.description_internal}</p>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {selectedStatus?.requires_reason && (
            <div>
              <label htmlFor="reason" className="text-xs">
                Reason<span className="text-red-500">*</span>
              </label>
              <Controller
                name="reason"
                control={control}
                rules={{ required: selectedStatus.requires_reason }}
                render={({ field }) => (
                  <ToggleGroup
                    type="single"
                    value={field.value}
                    onValueChange={field.onChange}
                    className="justify-start flex-wrap"
                  >
                    {selectedStatus.available_reasons.map((reason) => (
                      <ToggleItem key={reason} value={reason} variant="outline">
                        {reason}
                      </ToggleItem>
                    ))}
                  </ToggleGroup>
                )}
              />
            </div>
          )}

          <div>
            <label htmlFor="comments" className="text-xs">Comments</label>
            <Controller
              name="comments"
              control={control}
              render={({ field }) => (
                <Input {...field} />
              )}
            />
          </div>

          <p className="text-xs">
            Your username will be recorded when making this change.
          </p>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="thalo" disabled={!isValid}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeStatusModal;